<script>

import Service from '../../services/material-balance'
import Toolbar from 'primevue/toolbar';
import Utils from '../../common/Utils';
import CommonConstants from '../../common/Constants';
import config from '../../common/genericPanelConf';

import ResponsiveButton from '../../components/generic/ResponsiveButton.vue';

import { required } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import GenericDataTable from '../../components/generic/GenericDataTable.vue';
import GenericCustomMasterDataAutocompleteV2 from '../../components/generic/GenericCustomMasterDataAutocompleteV2.vue';
import GenericDateTimeMaskV2 from '../../components/generic/GenericDateTimeMaskV2.vue';
import GenericCustomAutocompleteV2 from '../../components/generic/GenericCustomAutocompleteV2.vue';
import GenericBooleanDropdown from '../../components/generic/GenericBooleanDropdown.vue';

export default {
    setup: () => ({ v$: useVuelidate() }),
    props: [],
    components: { Toolbar, ResponsiveButton, GenericDataTable, GenericCustomAutocompleteV2, GenericBooleanDropdown },
    data() {
        return {
            periodDate: Utils.setDay(new Date(), 1),
            lastElaborated: [],
            tabs: [],
            activeTab: 0,
            submitted: false,
            selectedLastElaborated: null,
            configuration: {},
            expandedRows: [],
            currentHeader: [],
            searchDialogVisible: false,
            searchData: {},
            refineryBalancePeriod: null,
            refineryBalanceDialogVisible: false,
            companyBalanceDialogVisible: false,
            publishData: {},
            publishDialogVisible: false,
            publishSubmitted: false,
            progressiveBalance: {},
            progressiveBalanceDialogVisible: false,
        }
    },
    created() {
        this.configuration = config.getConfiguration('MonthlyMaterialBalanceDetail');
    },
    validations() {
        return {
            publishData: {
                phase: {required},
                level: {required},
            }
        }
    },
    mounted() {
        this.$store.dispatch('security/setNavbarTitle', 'monthlyMaterialBalance.page_title');
        this.loadTabs();
        this.loadLastElaborated();
    },
    watch: {
        searchDialogVisible(newValue){
            if (!newValue) {
                this.searchData = {};
            }
        },
        progressiveBalanceDialogVisible(newValue) {
            if (!newValue) {
                this.progressiveBalance = {}
            }
        },
        publishDialogVisible(newValue){
            if (!newValue) {
                this.publishSubmitted = false;
            }
        },
        selectedLastElaborated(newValue) {
            if (newValue) {
                this.currentHeader = [newValue];
                this.publishData.level = this.currentHeader[0].idRegistryBalanceConfidentialLevel;
                this.publishData.phase = this.currentHeader[0].idRegistryBalancePhase;

                if(newValue.isProgressive) {
                    this.tabs.forEach((element, index) => {
                        let filters = {
                            isProgressive: true,
                            startDate: newValue.startDate,
                            endDate: newValue.endDate,
                            idRegistryCompany: element.idRegistryCompany,
                            idRegistryPlant: element.idRegistryPlant
                        };
                        this.$refs.MonthlyMaterialBalanceDetailGrid[index].filterFromView(filters, true);
                    });
                } else {
                    this.tabs.forEach((element, index) => {
                        let filters = {
                            isProgressive: false,
                            idMaterialBalanceSheetHeader: newValue.id,
                            idRegistryCompany: element.idRegistryCompany,
                            idRegistryPlant: element.idRegistryPlant
                        };
                        this.$refs.MonthlyMaterialBalanceDetailGrid[index].filterFromView(filters, true);
                    });
                }
            }
        },
    },
    computed: {
        constants() {
            return CommonConstants;
        },
        utils() {
            return Utils;
        },
        uiPermissions() {
            return this.$store.getters['security/uiPermissions'];
        },
    },
    methods: {
        loadTabs() {
            Service.getCompanySection({}, ({success, data}) => {
                if (success){
                    this.tabs = data.sort((a, b) => a.position - b.position);
                }
            })
        },
        loadLastElaborated(selectLast = false) {
            this.resetData();

            let filters = [];

            let params = {
                filter: JSON.stringify(filters),
                limit: 24,
                sort: JSON.stringify([
                    {
                        property: 'ExecutionDate', 
                        direction: 'desc'
                    }
                ])
            }

            this.$store.dispatch("generic/setLoading", true);
            Service.getMonthlyMaterialBalance(params, ({success, data}) => {
                this.$store.dispatch("generic/setLoading", false);
                if(success){
                    this.lastElaborated = data.map(({id, executionDate, balanceSheetDate, ...key}) => ({label: `ID: ${id} - ${Utils.formatMonthYear(balanceSheetDate)} - ${Utils.formatDateTime(Utils.dateToCurrentTimeZone(executionDate))}`, ...key, id, executionDate, balanceSheetDate}));
                    if (selectLast) {
                        this.selectedLastElaborated = this.lastElaborated[0];
                    }
                }
            })
        },
        resetSearch() {
            this.searchData = {};
        },
        resetData() {
            this.selectedLastElaborated = null;
            this.currentHeader = [];

            this.tabs.forEach((_, index) => {
                this.$refs.MonthlyMaterialBalanceDetailGrid[index].resetGrid();
            });
        },
        submitSearch() {
            this.resetData();
            
            let filters = [];

            if (this.searchData.elaboratedFrom) {
                filters.push({
                    operator: '>=',
                    value: Utils.formatDateForAPI(this.searchData.elaboratedFrom),
                    property: 'ExecutionDate',
                });
            }

            if (this.searchData.elaboratedTo) {
                filters.push({
                    operator: '<=',
                    value: Utils.formatDateForAPI(this.searchData.elaboratedTo),
                    property: 'ExecutionDate',
                });
            }

            if (this.searchData.periodFrom) {
                filters.push({
                    operator: '>=',
                    value: Utils.formatDateForAPI(this.searchData.periodFrom),
                    property: 'BalanceSheetDate',
                });
            }

            if (this.searchData.periodTo) {
                filters.push({
                    operator: '<=',
                    value: Utils.formatDateForAPI(this.searchData.periodTo),
                    property: 'BalanceSheetDate',
                });
            }
            
            if (this.searchData.profile) {
                filters.push({
                    operator: 'eq',
                    value: this.searchData.profile,
                    property: 'IdRegistryBalanceSheetAccountType',
                });
            }

            if (this.searchData.type) {
                filters.push({
                    operator: 'eq',
                    value: this.searchData.type,
                    property: 'IdRegistryBalanceSheetType',
                });
            }

            if (this.searchData.phase) {
                filters.push({
                    operator: 'eq',
                    value: this.searchData.phase,
                    property: 'IdRegistryBalancePhase',
                });
            }

            if (this.searchData.level) {
                filters.push({
                    operator: 'eq',
                    value: this.searchData.level,
                    property: 'IdRegistryBalanceConfidentialLevel',
                });
            }

            let params = {
                filter: JSON.stringify(filters),
                limit: 24,
                isLastElaborated: !!this.searchData.isLastElaborated,
                sort: JSON.stringify([
                    {
                        property: 'ExecutionDate', 
                        direction: 'desc'
                    }
                ])
            }
            this.$store.dispatch("generic/setLoading", true);
            Service.getMonthlyMaterialBalance(params, ({success, data}) => {
                this.$store.dispatch("generic/setLoading", false);
                this.searchDialogVisible = false;
                if(success){
                    this.lastElaborated = data.map(({id, executionDate, balanceSheetDate, ...key}) => ({label: `ID: ${id} - ${Utils.formatMonthYear(balanceSheetDate)} - ${Utils.formatDateTime(executionDate)}`, ...key, id, executionDate, balanceSheetDate}));
                    if(this.lastElaborated.length === 1) {
                        this.selectedLastElaborated = this.lastElaborated[0];
                    }
                }
            })
        },
        runRefineryBalance(){
            this.$store.dispatch("generic/setLoading", true);
            Service.calculateRefineryBalance({balanceSheetDate: Utils.formatDateForAPI(this.refineryBalancePeriod)}, ({success}) => {
                this.$store.dispatch("generic/setLoading", false);
                this.refineryBalanceDialogVisible = false;
                this.refineryBalancePeriod = null;
                if(success){
                    this.$toast.add({ severity: 'success', summary: 'Success Message', detail: this.$t('messages.update_message_success'), life: 3000 });
                    this.loadLastElaborated(true);
                } else {
                    this.$toast.add({ detail: response?.message && response?.message != '' ? response?.message : this.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                }
            })
        },
        runCompanyBalance() {
            this.$store.dispatch("generic/setLoading", true);
            Service.calculateCompanyBalance({idRefineryMaterialBalanceSheetHeader: this.currentHeader[0].id}, ({success}) => {
                this.$store.dispatch("generic/setLoading", false);
                this.companyBalanceDialogVisible = false;
                if(success){
                    this.$toast.add({ severity: 'success', summary: 'Success Message', detail: this.$t('messages.update_message_success'), life: 3000 });
                    this.loadLastElaborated(true);
                } else {
                    this.$toast.add({ detail: response?.message && response?.message != '' ? response?.message : this.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                }
            })
        },
        publish(){
            this.publishSubmitted = true;
            if (!this.v$.publishData.$invalid) {
                this.$store.dispatch("generic/setLoading", true);
                Service.publishBalance({id: this.currentHeader[0].id, idRegistryBalanceConfidentialLevel: this.publishData.level, idRegistryBalancePhase: this.publishData.phase}, (response) => {
                    this.$store.dispatch("generic/setLoading", false);
                    this.companyBalanceDialogVisible = false;
                    if(response.success){
                        this.publishDialogVisible = false;
                        this.$toast.add({ severity: 'success', summary: 'Success Message', detail: this.$t('messages.update_message_success'), life: 3000 });
                        this.selectedLastElaborated.idRegistryBalancePhase = this.publishData.phase;
                        this.selectedLastElaborated.idRegistryBalanceConfidentialLevel = this.publishData.level;
                    } else {
                        this.$toast.add({ detail: response?.message && response?.message != '' ? response?.message : this.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                    }
                })
            }
        },
        runProgressiveBalance() {
            this.$store.dispatch("generic/setLoading", true);
            const startDate = this.progressiveBalance.periodFrom;
            const endDate = this.progressiveBalance.periodTo;

            Service.getProgressiveMaterialBalance({startDate, endDate}, (response) => {
                this.$store.dispatch("generic/setLoading", false);
                this.progressiveBalanceDialogVisible = false;
                if(response.success){
                    this.resetData();
                    this.$toast.add({ severity: 'success', summary: 'Success Message', detail: this.$t('messages.update_message_success'), life: 3000 });
                    this.lastElaborated = response.data.map(({id, executionDate, balanceSheetDate, ...key}) => ({label: `ID: ${id} - ${Utils.formatMonthYear(balanceSheetDate)} - ${Utils.formatDateTime(executionDate)}`, ...key, id, executionDate, balanceSheetDate, isProgressive: true, startDate, endDate}));
                } else {
                    this.$toast.add({ detail: response?.message && response?.message != '' ? response?.message : this.$t('messages.generic_error_message'), life: 3000, severity: 'error' });
                }
            })
        },
        exportProgressiveBalance() {
                let params = {
                    idMaterialBalanceSheetHeader: this.selectedLastElaborated.id,
                    idRegistryPlant: this.tabs[this.activeTab].idRegistryPlant,
                    idRegistryCompany: this.tabs[this.activeTab].idRegistryCompany,
                };
                
                this.$store.dispatch("generic/setLoading", true);
                Service.exportMaterialBalanceAggregateBalance(params, response => {
                    this.$store.dispatch("generic/setLoading", false);
                    if(response.success){    
                        var fileURL = window.URL.createObjectURL(new Blob([response.data]));
                        var fileLink = document.createElement('a');
                    
                        fileLink.href = fileURL;
                        fileLink.setAttribute('download', params.idMaterialBalanceSheetHeader + '.xlsx');
                        document.body.appendChild(fileLink);
                    
                        fileLink.click();
                    }else{
                        this.$toast.add({detail: response.message && response.message != '' ? response.message :  this.$t('messages.generic_error_message'), life:3000, severity:'error'});
                    }
                })
        },
    },
}

</script>

<template>
    <main>
        <Dialog v-model:visible="searchDialogVisible" :style="{ width: '450px' }" modal>
            <template #header>
                <h3>{{$t('monthlyMaterialBalance.search')}}</h3>
            </template>
            <div class="grid">
                <div class="col-6 field">
                    <label>{{ $t('monthlyMaterialBalance.elaboratedFrom') }}</label>
                    <Calendar
                        class="p-inputtext-sm w-full"
                        placeholder="DD/MM/AAAA"
                        v-model="searchData.elaboratedFrom"
                        dateFormat="dd/mm/yy" :showTime="false"
                        :showIcon="true" :showButtonBar="true" :yearNavigator="true" />
                </div>
                <div class="col-6 field">
                    <label>{{ $t('monthlyMaterialBalance.elaboratedTo') }}</label>
                    <Calendar
                        class="p-inputtext-sm w-full"
                        placeholder="DD/MM/AAAA"
                        v-model="searchData.elaboratedTo"
                        dateFormat="dd/mm/yy" :showTime="false"
                        :showIcon="true" :showButtonBar="true" :yearNavigator="true" />
                </div>
                <div class="col-6 field">
                    <label>{{ $t('monthlyMaterialBalance.periodFrom') }}</label>
                    <Calendar
                        class="p-inputtext-sm w-full"
                        placeholder="MM/AAAA"
                        v-model="searchData.periodFrom"
                        view="month" dateFormat="mm/yy" :showTime="false"
                        :showIcon="true" :showButtonBar="true" :yearNavigator="true" />
                </div>
                <div class="col-6 field">
                    <label>{{ $t('monthlyMaterialBalance.periodTo') }}</label>
                    <Calendar
                        class="p-inputtext-sm w-full"
                        placeholder="MM/AAAA"
                        v-model="searchData.periodTo"
                        view="month" dateFormat="mm/yy" :showTime="false"
                        :showIcon="true" :showButtonBar="true" :yearNavigator="true" />
                </div>
                <div class="col-6 field">
                    <label>{{ $t('monthlyMaterialBalance.profile') }}</label>
                    <GenericCustomAutocompleteV2 
                        class="p-inputtext-sm w-full"
                        v-model="searchData.profile"
                        :format="{ label: 'label', value: 'value' }"
                        api="masterdata/balance-sheet-account-type-combo" />
                </div>
                <div class="col-6 field">
                    <label>{{ $t('monthlyMaterialBalance.type') }}</label>
                    <GenericCustomAutocompleteV2 
                        class="p-inputtext-sm w-full"
                        v-model="searchData.type"
                        :format="{ label: 'label', value: 'value' }"
                        api="masterdata/balance-sheet-type-combo" />
                </div>
                <div class="col-6 field">
                    <label>{{ $t('monthlyMaterialBalance.phase') }}</label>
                    <GenericCustomAutocompleteV2 
                        class="p-inputtext-sm w-full"
                        v-model="searchData.phase"
                        :format="{ label: 'label', value: 'value' }"
                        api="masterdata/balance-phase-combo" />
                </div>
                <div class="col-6 field">
                    <label>{{ $t('monthlyMaterialBalance.level') }}</label>
                    <GenericCustomAutocompleteV2 
                        class="p-inputtext-sm w-full"
                        v-model="searchData.level"
                        :format="{ label: 'label', value: 'value' }"
                        api="masterdata/balance-confidential-level-combo" />
                </div>
                <div class="col-6 field">
                    <label>{{ $t('monthlyMaterialBalance.isLastElaborated') }}</label>
                    <GenericBooleanDropdown 
                        class="p-inputtext-sm w-full"
                        v-model="searchData.isLastElaborated" />
                </div>
            </div>
            <template #footer>
                <ResponsiveButton :label="$t('btn.reset')"
                    icon="pi pi-sync" class="p-button-sm p-button-outlined m-1"
                    @click="resetSearch" />
                <ResponsiveButton :label="$t('btn.search')"
                    icon="pi pi-search" class="p-button-sm m-1"
                    @click="submitSearch" />
            </template>
        </Dialog>
        <Dialog v-model:visible="refineryBalanceDialogVisible" :style="{ width: '350px' }" modal>
            <template #header>
                <h3>{{$t('monthlyMaterialBalance.runRefineryBalance')}}</h3>
            </template>
            <div class="grid">
                <div class="col-12 field">
                    <label>{{ $t('monthlyMaterialBalance.period') }}</label>
                    <Calendar
                        class="p-inputtext-sm w-full"
                        placeholder="MM/AAAA"
                        v-model="refineryBalancePeriod"
                        view="month" dateFormat="mm/yy" :showTime="false"
                        :showIcon="true" :showButtonBar="true" :yearNavigator="true" />
                </div>
            </div>
            <template #footer>
                <ResponsiveButton :label="$t('btn.calculate')"
                    :disabled="!refineryBalancePeriod"
                    icon="pi pi-send" class="p-button-sm m-1"
                    @click="runRefineryBalance" />
            </template>
        </Dialog>
        <Dialog v-model:visible="progressiveBalanceDialogVisible" :style="{ width: '350px' }" modal>
            <template #header>
                <h3>{{$t('monthlyMaterialBalance.runProgressiveBalance')}}</h3>
            </template>
            <div class="grid">
                <div class="col-12 field">
                    <label>{{ $t('monthlyMaterialBalance.periodFrom') }}</label>
                    <Calendar
                        class="p-inputtext-sm w-full"
                        placeholder="MM/AAAA"
                        v-model="progressiveBalance.periodFrom"
                        view="month" dateFormat="mm/yy" :showTime="false"
                        :showIcon="true" :showButtonBar="true" :yearNavigator="true" />
                </div>
                <div class="col-12 field">
                    <label>{{ $t('monthlyMaterialBalance.periodTo') }}</label>
                    <Calendar
                        class="p-inputtext-sm w-full"
                        placeholder="MM/AAAA"
                        v-model="progressiveBalance.periodTo"
                        view="month" dateFormat="mm/yy" :showTime="false"
                        :showIcon="true" :showButtonBar="true" :yearNavigator="true" />
                </div>
            </div>
            <template #footer>
                <ResponsiveButton :label="$t('btn.calculate')"
                    :disabled="(!progressiveBalance.periodTo && !progressiveBalance.periodFrom)"
                    icon="pi pi-play" class="p-button-sm m-1"
                    @click="runProgressiveBalance" />
            </template>
        </Dialog>
        <Dialog v-model:visible="companyBalanceDialogVisible" :style="{ width: '350px' }" modal>
            <template #header>
                <h3>{{$t('monthlyMaterialBalance.runCompanyBalance')}}</h3>
            </template>
            <div class="grid">
                <div class="col-12 field">
                    <label>{{ $t('monthlyMaterialBalance.period') }}</label>
                    <Calendar
                        class="p-inputtext-sm w-full"
                        placeholder="MM/AAAA"
                        :disabled="true"
                        :model-value="utils.formatMonthYear(currentHeader[0].balanceSheetDate)"
                        view="month" dateFormat="mm/yy" :showTime="false"
                        :showIcon="true" :showButtonBar="true" :yearNavigator="true" />
                </div>
                <div class="col-12 field">
                    <label>{{ $t('monthlyMaterialBalance.refineryId') }}</label>
                    <InputText
                        class="p-inputtext-sm w-full"
                        :disabled="true"
                        v-model="currentHeader[0].id" />
                </div>
            </div>
            <template #footer>
                <ResponsiveButton :label="$t('btn.calculate')"
                    icon="pi pi-play" class="p-button-sm m-1"
                    @click="runCompanyBalance" />
            </template>
        </Dialog>
        <Dialog v-model:visible="publishDialogVisible" :style="{ width: '350px' }" modal>
            <template #header>
                <h3>{{$t('monthlyMaterialBalance.publish')}}</h3>
            </template>
            <div class="grid">
                <div class="col-12 field">
                    <label>{{ $t('id') }}</label>
                    <InputText
                        class="p-inputtext-sm w-full"
                        :disabled="true"
                        :model-value="currentHeader?.[0].id" />
                </div>
                <div class="col-12 field">
                    <label>{{ $t('monthlyMaterialBalance.period') }}</label>
                    <Calendar
                        class="p-inputtext-sm w-full"
                        placeholder="MM/AAAA"
                        :disabled="true"
                        :model-value="utils.formatMonthYear(currentHeader?.[0].balanceSheetDate)"
                        view="month" dateFormat="mm/yy" :showTime="false"
                        :showIcon="true" :showButtonBar="true" :yearNavigator="true" />
                </div>
                <div class="col-12 field">
                    <label :class="{ 'p-error': v$.publishData.phase.$invalid && publishSubmitted }">{{ $t('monthlyMaterialBalance.phase') }}</label>
                    <GenericCustomAutocompleteV2 
                        :class="{'p-invalid':v$.publishData.phase.$invalid && publishSubmitted, 'w-full p-inputtext-sm ': true}"
                        v-model="publishData.phase"
                        :format="{ label: 'label', value: 'value' }"
                        api="masterdata/balance-phase-combo" />
                    <div v-if="(v$.publishData.phase.$invalid && publishSubmitted) || v$.publishData.phase.$pending.$response">
                        <small class="p-error" v-for="error in v$.publishData.phase.$silentErrors" :key="error.$validator">
                            {{error.$message}}
                        </small>
                    </div>
                </div>
                <div class="col-12 field">
                    <label :class="{ 'p-error': v$.publishData.level.$invalid && publishSubmitted }">{{ $t('monthlyMaterialBalance.level') }}</label>
                    <GenericCustomAutocompleteV2 
                        :class="{'p-invalid':v$.publishData.level.$invalid && publishSubmitted, 'w-full p-inputtext-sm ': true}"
                        v-model="publishData.level"
                        :format="{ label: 'label', value: 'value' }"
                        api="masterdata/balance-confidential-level-combo" />
                    <div v-if="(v$.publishData.level.$invalid && publishSubmitted) || v$.publishData.level.$pending.$response">
                        <small class="p-error" v-for="error in v$.publishData.level.$silentErrors" :key="error.$validator">
                            {{error.$message}}
                        </small>
                    </div>
                </div>
            </div>
            <template #footer>
                <ResponsiveButton :label="$t('monthlyMaterialBalance.publish')"
                    icon="pi pi-send" class="p-button-sm m-1"
                    @click="publish" />
            </template>
        </Dialog>
        <Toolbar>
            <template #start>
                <div class="col-12">
                    <label>{{ $t('monthlyMaterialBalance.lastElaborated') }}</label>
                    <Dropdown
                        :options="lastElaborated"
                        placeholder="ID: ___ - ___/___/_____ - ___/_____"
                        v-model="selectedLastElaborated"
                        name="selectedLastElaborated"
                        id="selectedLastElaborated"
                        optionLabel="label"
                        class="w-full" />
                </div>
            </template>
            <template #end>
                <div class="col-12">
                    <ResponsiveButton :label="$t('btn.refresh')"
                        icon="pi pi-sync" class="p-button-sm p-button-outlined m-1"
                        @click="loadLastElaborated(false)" />
                    <ResponsiveButton :label="$t('btn.search')"
                        :disabled="!uiPermissions('monthlyMaterialBalanceSearchButton').isEnabled"
                        icon="pi pi-search" class="p-button-sm p-button-outlined m-1"
                        @click="searchDialogVisible = true" />
                    <ResponsiveButton :label="$t('btn.calculate')"
                        :disabled="!uiPermissions('monthlyMaterialBalanceCalculateButton').isEnabled"
                        icon="pi pi-table" class="p-button-sm p-button-outlined m-1"
                        @click="refineryBalanceDialogVisible = true" />
                    <ResponsiveButton :label="$t('monthlyMaterialBalance.btn.progressive')"
                        :disabled="!uiPermissions('monthlyMaterialBalanceProgressiveButton').isEnabled"
                        icon="pi pi-list" class="p-button-sm p-button-outlined m-1"
                        @click="progressiveBalanceDialogVisible = true" />
                </div>
            </template>
        </Toolbar>
        <Panel header="Header" class="mt-4">
            <ResponsiveButton :label="$t('monthlyMaterialBalance.btn.calculateCompanies')"
                :disabled="!currentHeader?.[0] || (currentHeader?.[0] && currentHeader[0].registryBalanceSheetType != 'Raffineria') || !uiPermissions('monthlyMaterialBalanceCalculateCompaniesButton').isEnabled"
                icon="pi pi-cog" class="p-button-sm p-button-outlined mb-3 mr-2"
                @click="companyBalanceDialogVisible = true" />
            <ResponsiveButton :label="$t('btn.publish')"
                :disabled="!currentHeader?.[0] || !uiPermissions('monthlyMaterialBalancePublishButton').isEnabled"
                icon="pi pi-send" class="p-button-sm p-button-outlined mb-3"
                @click="publishDialogVisible = true" />
            <!-- :disabled="!currentHeader?.[0] || (currentHeader?.[0] && currentHeader[0].registryBalanceSheetType != 'Raffineria') || !uiPermissions('monthlyMaterialBalancePublishButton').isEnabled" -->
            <DataTable :value="currentHeader" v-model:expandedRows="expandedRows" :dataKey="currentHeader.id" showGridlines stripedRows>
                <Column field="id" :header="$t('id')"></Column>
                <Column field="balanceSheetDate" :header="$t('monthlyMaterialBalance.period')">
                    <template #body="element">
                        {{ utils.formatMonthYear(element.data.balanceSheetDate) }}
                    </template></Column>
                <Column field="registryBalanceSheetType" :header="$t('monthlyMaterialBalance.profile')"></Column>
                <Column field="registryBalanceSheetAccountType" :header="$t('monthlyMaterialBalance.type')"></Column>
                <Column field="registryBalancePhase" :header="$t('monthlyMaterialBalance.phase')">
                    <template #body="element">
                        <GenericCustomAutocompleteV2 
                            class="p-inputtext-sm w-full ui-autocomplete-disabled"
                            v-model="element.data.idRegistryBalancePhase"
                            :format="{ label: 'label', value: 'value' }"
                            api="masterdata/balance-phase-combo" />
                   </template>
                </Column>
                <Column field="registryBalanceConfidentialLevel" :header="$t('monthlyMaterialBalance.level')">
                    <template #body="element">
                        <GenericCustomAutocompleteV2 
                            class="p-inputtext-sm w-full ui-autocomplete-disabled"
                            v-model="element.data.idRegistryBalanceConfidentialLevel"
                            :format="{ label: 'label', value: 'value' }"
                            api="masterdata/balance-confidential-level-combo" />
                    </template>
                </Column>
                <Column field="executionDate" :header="$t('monthlyMaterialBalance.elaboratedOn')">
                    <template #body="element">
                        {{ utils.formatDateTime(element.data.executionDate) }}
                    </template>
                </Column>
                <template #empty>
                    {{ $t('noRecordsFound') }}
                </template>
            </DataTable>
        </Panel>
        <Panel header="Details" class="mt-4">
            <div class="col-12">
                <ResponsiveButton :label="$t('btn.print')"
                    :disabled="!selectedLastElaborated || !uiPermissions('monthlyMaterialBalancePrintDetailButton').isEnabled"
                    icon="pi pi-file-pdf" class="p-button-sm p-button-outlined m-1"
                    @click="exportProgressiveBalance" />
            </div>
            <div class="col-12">
                <TabView v-model:activeIndex="activeTab">
                    <TabPanel v-for="tab in tabs" :header="tab.description" :key="tab.description" >
                        <div style="height: 70vh;">
                            <GenericDataTable :configuration="configuration" :category="'material-balance'" :name="'monthly-detail'"
                                ref="MonthlyMaterialBalanceDetailGrid" />
                        </div>
                    </TabPanel>
                </TabView>
            </div>
        </Panel>
    </main>
</template>

<style scoped>
    .ui-autocomplete-disabled {
        pointer-events: none;
        cursor: default !important;
        opacity: 0.8;
    }
</style>